import trimEnd from 'lodash-es/trimEnd'
import fetch from 'node-fetch'

import { IFastApiProvider } from './interface'

import {
  ILogger, IBTCUnspent,
  NetworkError, RPCError,
  RPCNode, helper,
} from '@one-chain/core'

/**
 * BlockABC 接口 Provider
 *
 * @export
 * @class BlockABC
 * @implements {IFastApiProvider}
 */
export class BlockABC implements IFastApiProvider {
  protected logger: ILogger
  protected endpoint: string

  readonly supportBatch = true
  readonly rateLimit = 3

  static instance: BlockABC

  constructor (rpcnode: RPCNode, logger: ILogger) {
    if (BlockABC.instance) {
      return BlockABC.instance
    }

    this.logger = logger
    this.endpoint = trimEnd(rpcnode.baseUrl, '/')

    BlockABC.instance = this
  }

  async ping () {
    return this.get('/recommended_fee_rates')
  }

  async getAddresses (addresses: string[]): Promise<{ address: string, txCount: number }[]> {
    const results = await this.post('/address', addresses)

    return results.map(val => {
      return {
        address: val.address,
        txCount: val.used ? 1 : 0,
      }
    })
  }

  async getUnspentOfAddresses (addresses: string[], onlyConfirmed = false): Promise<IBTCUnspent[]> {
    const results = await this.post('/address/unspents', addresses)
    return results.map(val => {
      return {
        txId: val.txid,
        address: val.address,
        vout: val.vout_index,
        value: val.value,
      }
    })
  }

  async pushTransaction (rawTransaction: string): Promise<string> {
    return this.post('/send_raw_transaction', { tx: rawTransaction })
  }

  protected buildUrl (path: string, query: any = null) {
    let url = this.endpoint + path

    if (query) {
      url += '?' + new URLSearchParams(query)
    }

    return url
  }

  protected async get (path, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      this.logger.trace('BTC - BlockABC.get Request data: ', url)

      const startAt = Date.now()
      const res = await helper.timeout<Response>(5000, fetch(url, {
        method: 'GET',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        },
      }))
      this.logger.trace('BTC - BlockABC.get Request benchmark(in ms):', Date.now() - startAt)

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
          if (ret.errno !== 0) {
            this.logger.error('BTC - BlockABC.get Response error:', path, ret.errno, ret.errmsg)
            throw new RPCError(ret.errno, ret.errmsg, null)
          }
        }
        !PROD && this.logger.trace('BTC - BlockABC.get Response data:', path, ret)
      }
      else {
        this.logger.error('BTC - BlockABC.get Network error:', `${res.status} ${res.statusText}`)
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        this.logger.error('BTC - BlockABC.get Unknown error:', path, err.message)
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret.data
  }

  protected async post (path, data, query: any = null): Promise<any> {
    let ret
    try {
      const url = this.buildUrl(path, query)
      this.logger.trace('BTC - BlockABC.post Request data: ', url, data)

      const startAt = Date.now()
      const res = await helper.timeout<Response>(5000, fetch(url, {
        method: 'POST',
        // @ts-ignore
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }))
      this.logger.trace('BTC - BlockABC.post Request benchmark(in ms):', Date.now() - startAt)

      if (res.ok) {
        ret = await res.text()
        if (ret) {
          ret = JSON.parse(ret)
          if (ret.errno !== 0) {
            this.logger.error('BTC - BlockABC.post Response error:', path, ret.errno, ret.errmsg)
            throw new RPCError(ret.errno, ret.errmsg, null)
          }
        }
        !PROD && this.logger.trace('BTC - BlockABC.post Response data: ', ret)
      }
      else {
        this.logger.error('BTC - BlockABC.post Network error:', path, `${res.status} ${res.statusText}`)
        throw NetworkError.fromCode(res.status, `${res.status} ${res.statusText}`)
      }
    }
    catch (err) {
      if (err instanceof NetworkError) {
        throw err
      }
      else {
        this.logger.error('BTC - BlockABC.post Unknown error:', path, err.message)
        throw NetworkError.fromCode(0, err.message)
      }
    }

    return ret.data
  }
}

export default BlockABC
